/**
 * SMS Counters Page — Launch Protection
 * Master Admin view of today's SMS volume + caps + threshold alerts.
 * Feb 2026 launch hardening (Crossroads request: monitor SMS spend before
 * scaling to 50+ dealers via shared GHL number).
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { MessageSquare, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";

const SmsCountersPage = () => {
  const { apiFetch } = useAuth();
  const { error: showError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch("/admin/sms-counters");
      setData(res.data);
    } catch (e) {
      showError("Failed to load SMS counters");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, showError]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="p-6" data-testid="sms-counters-loading">
        <div className="h-24 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  const globalPct = data.caps.global > 0 ? Math.round((data.global.sent_count / data.caps.global) * 100) : 0;
  const globalLevel = globalPct >= 100 ? "critical" : globalPct >= 80 ? "warning" : globalPct >= 50 ? "info" : "ok";

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="sms-counters-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="text-blue-600" /> SMS Volume & Cost Protection
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Real-time SMS sends across all dealers — today (UTC). Alerts go to <span className="font-semibold">{data.alert_recipient}</span>.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="sms-counters-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Global cap */}
      <Card className="mb-6" data-testid="sms-global-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Platform-wide today
            {globalLevel === "critical" && <Badge className="bg-red-100 text-red-800">100% — auto-disabled</Badge>}
            {globalLevel === "warning" && <Badge className="bg-orange-100 text-orange-800">80% threshold</Badge>}
            {globalLevel === "info" && <Badge className="bg-amber-100 text-amber-800">50% threshold</Badge>}
            {globalLevel === "ok" && <Badge className="bg-green-100 text-green-800">healthy</Badge>}
          </CardTitle>
          <CardDescription>Daily UTC. Counter resets at midnight.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Metric label="Sent today" value={data.global.sent_count} />
            <Metric label="Daily cap" value={data.caps.global} />
            <Metric label="Blocked (abuse)" value={data.global.blocked_count} />
            <Metric label="Usage %" value={`${globalPct}%`} />
          </div>
          {/* Visual bar */}
          <div className="mt-4 w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all ${
                globalLevel === "critical" ? "bg-red-500"
                : globalLevel === "warning" ? "bg-orange-500"
                : globalLevel === "info" ? "bg-amber-500"
                : "bg-green-500"
              }`}
              style={{ width: `${Math.min(globalPct, 100)}%` }}
              data-testid="sms-global-progress"
            />
          </div>
        </CardContent>
      </Card>

      {/* Caps reference */}
      <Card className="mb-6" data-testid="sms-caps-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="text-blue-600" size={18} /> Active limits
          </CardTitle>
          <CardDescription>Configured via env vars. Edit and restart to change.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
            <Limit label="Global daily" value={`${data.caps.global} SMS`} />
            <Limit label="Per-dealer daily" value={`${data.caps.per_dealer} SMS`} />
            <Limit label="IP / hour" value={`${data.caps.ip_per_hour} req`} />
            <Limit label="Phone / day" value={`${data.caps.phone_per_day} req`} />
            <Limit label="Dealer attempts / day" value={`${data.caps.dealer_per_day} req`} />
          </div>
        </CardContent>
      </Card>

      {/* Top dealers today */}
      <Card data-testid="sms-top-dealers-card">
        <CardHeader>
          <CardTitle>Top dealers by SMS volume today</CardTitle>
          <CardDescription>Sorted by sent count. Blocked = attempts rejected by rate-limiter.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.top_dealers.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No SMS sent yet today.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sms-top-dealers-table">
                <thead>
                  <tr className="text-left border-b border-gray-200">
                    <th className="py-2 px-3 font-medium text-gray-600">Dealer</th>
                    <th className="py-2 px-3 font-medium text-gray-600">Sent</th>
                    <th className="py-2 px-3 font-medium text-gray-600">Blocked</th>
                    <th className="py-2 px-3 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_dealers.map((d) => {
                    const pct = data.caps.per_dealer > 0 ? Math.round((d.sent_count / data.caps.per_dealer) * 100) : 0;
                    const level = pct >= 100 ? "critical" : pct >= 80 ? "warning" : pct >= 50 ? "info" : "ok";
                    return (
                      <tr key={d.scope_key} className="border-b border-gray-100" data-testid={`sms-row-${d.scope_key}`}>
                        <td className="py-2 px-3">{d.dealer_name || d.scope_key}</td>
                        <td className="py-2 px-3 font-mono">{d.sent_count} / {data.caps.per_dealer}</td>
                        <td className="py-2 px-3 font-mono text-gray-500">{d.blocked_count}</td>
                        <td className="py-2 px-3">
                          {level === "critical" && <Badge className="bg-red-100 text-red-800"><AlertTriangle size={12} className="mr-1" /> Capped</Badge>}
                          {level === "warning" && <Badge className="bg-orange-100 text-orange-800">80%</Badge>}
                          {level === "info" && <Badge className="bg-amber-100 text-amber-800">50%</Badge>}
                          {level === "ok" && <Badge className="bg-green-100 text-green-800">OK</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
    <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
  </div>
);

const Limit = ({ label, value }) => (
  <div className="border border-gray-200 rounded-lg p-3">
    <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
    <div className="text-sm font-semibold text-gray-900 mt-1">{value}</div>
  </div>
);

export default SmsCountersPage;
